<template>
	<div id="wrapper">
		<SidebarView
			:memacc="true"
			:memaccVisible="true"
			:interestSetup="true"
		/>
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="mx-3">
					<div class="card card-shape home-box mb-3">
						<div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
							<h6 class="m-0 fw-bold text-green">Interest Setup</h6>
							<b-button
								class="akkurate-green-btn btn-size text-white"
								@click="openCreateModal"
							>
								<font-awesome-icon class="me-1" :icon="['fas', 'plus']" />
								Add New
							</b-button>
						</div>
						<div class="card-body">
							<p class="small text-muted mb-3">
								Configure product interest payout rules. Use <strong>Pay Interest</strong> to run a setup for a period,
								or trigger automated runs by frequency.
							</p>

							<div class="row g-2 mb-3 align-items-end">
								<div class="col-md-4">
									<label class="akkurate-dark small">Search</label>
									<b-form-input
										v-model="filters.filter"
										placeholder="Name or code..."
										class="field-container"
										@keyup.enter="loadSetups"
									/>
								</div>
								<div class="col-md-3">
									<label class="akkurate-dark small">Status</label>
									<b-form-select
										v-model="filters.is_active"
										:options="activeOptions"
										class="field-container"
										@change="loadSetups"
									/>
								</div>
								<div class="col-md-auto">
									<b-button class="akkurate-green-btn btn-size text-white" @click="loadSetups">
										<font-awesome-icon class="me-1" :icon="['fas', 'search']" />
										Apply
									</b-button>
								</div>
							</div>

							<b-table
								striped
								bordered
								responsive
								:busy="isBusy"
								:items="rows"
								:fields="tableFields"
								show-empty
								class="table_text_size"
							>
								<template #table-busy>
									<div class="text-center text-danger my-2">
										<b-spinner class="align-middle" />
										<strong>Loading...</strong>
									</div>
								</template>
								<template #cell(is_active)="data">
									<span
										:class="data.item.is_active ? 'badge akkurate-green-bg' : 'badge akkurate-gray'"
									>
										{{ data.item.is_active ? "Active" : "Inactive" }}
									</span>
								</template>
								<template #cell(interest_method)="data">
									{{ formatMethod(data.item.interest_method) }}
								</template>
								<template #cell(frequency)="data">
									{{ formatFrequency(data.item.frequency) }}
								</template>
								<template #cell(actions)="data">
									<div class="d-flex flex-wrap gap-1">
										<b-button class="btn-size" variant="outline-secondary" @click="openEditModal(data.item)">
											<font-awesome-icon class="me-1" :icon="['fas', 'edit']" />
											Edit
										</b-button>
										<b-button class="btn-size" variant="outline-info" @click="openViewModal(data.item)">
											<font-awesome-icon class="me-1" :icon="['fas', 'eye']" />
											View
										</b-button>
										<b-button class="btn-size" variant="outline-info" @click="openHistoryModal(data.item)">
											<font-awesome-icon class="me-1" :icon="['fas', 'history']" />
											History
										</b-button>
										<b-button class="btn-size" variant="outline-danger" @click="confirmDelete(data.item)">
											<font-awesome-icon class="me-1" :icon="['fas', 'trash']" />
											Delete
										</b-button>
									</div>
								</template>
							</b-table>

							<div class="d-flex justify-content-between align-items-center mt-2 px-1 flex-wrap gap-2">
								<b-pagination
									v-model="currentPage"
									:total-rows="totalRecords"
									:per-page="perPage"
									@change="onPageChange"
									class="mb-0"
								/>
								<div class="small akkurate-dark">
									{{ currentPage }} / {{ Math.max(1, Math.ceil(totalRecords / perPage)) }} — {{ totalRecords }} total
								</div>
								<b-form-group label="Per page" class="mb-0">
									<b-form-select
										v-model="perPage"
										class="field-container"
										:options="perPageOptions"
										@change="loadSetups"
									/>
								</b-form-group>
							</div>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>
		<ChatBot />
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>

		<!-- Create / Edit -->
		<b-modal
			v-model="showFormModal"
			size="lg"
			:title="editingId ? 'Edit interest setup' : 'Create interest setup'"
			@hidden="resetForm"
		>
			<b-overlay :show="formLoading" rounded="sm">
				<div class="row g-2">
					<div class="col-md-6">
						<label class="akkurate-dark small">Name <span class="text-danger">*</span></label>
						<b-form-input v-model="form.name" class="field-container" />
					</div>
					<div class="col-md-6">
						<label class="akkurate-dark small">Code</label>
						<b-form-input v-model="form.code" class="field-container" />
					</div>
					<div class="col-12">
						<label class="akkurate-dark small">Description</label>
						<b-form-textarea v-model="form.description" rows="2" class="field-container" />
					</div>
					<div class="col-md-6">
						<b-form-checkbox v-model="form.is_active"> Active </b-form-checkbox>
					</div>

					<div class="col-md-6">
						<label class="akkurate-dark small">Interest method <span class="text-danger">*</span></label>
						<b-form-select v-model="form.interest_method" :options="methodOptions" class="field-container" />
					</div>

					<div v-if="form.interest_method === 'flat_amount'" class="col-md-6">
						<label class="akkurate-dark small">Flat amount (GHS) <span class="text-danger">*</span></label>
						<b-form-input v-model.number="form.interest_flat_amount" type="number" step="any" min="0" class="field-container" />
					</div>
					<div v-if="form.interest_method === 'percent_of_balance'" class="col-md-6">
						<label class="akkurate-dark small">Percent (0–100] <span class="text-danger">*</span></label>
						<b-form-input v-model.number="form.interest_percent" type="number" step="any" min="0" max="100" class="field-container" />
					</div>

					<div class="col-md-6">
						<label class="akkurate-dark small">Minimum balance (GHS) <span class="text-danger">*</span></label>
						<b-form-input v-model.number="form.minimum_balance" type="number" step="any" min="0" class="field-container" />
					</div>

					<div class="col-md-6">
						<label class="akkurate-dark small">Frequency <span class="text-danger">*</span></label>
						<b-form-select v-model="form.frequency" :options="frequencyOptions" class="field-container" />
					</div>

					<div class="col-12">
						<label class="akkurate-dark small">Eligible product types <span class="text-danger">*</span></label>
						<multiselect
							v-model="form.product_account_types"
							:options="accountTypes"
							label="name"
							track-by="id"
							:multiple="true"
							placeholder="Select products"
							class="field-container"
						/>
						<div v-if="formErrorProducts" class="small text-danger mt-1">{{ formErrorProducts }}</div>
					</div>

					<div class="col-12">
						<label class="akkurate-dark small">
							Debit COA (Cost of Funds) <span class="text-danger">*</span>
						</label>
						<multiselect
							v-model="form.debit_coa_subhead"
							:options="costOfFundsCoa"
							:internal-search="false"
							@search-change="onCoaSearch"
							label="name"
							track-by="id"
							placeholder="Search COA..."
							class="field-container"
							:custom-label="coaLabel"
						/>
						<div v-if="formErrorCoa" class="small text-danger mt-1">{{ formErrorCoa }}</div>
					</div>

					<div v-if="formError" class="col-12">
						<div class="alert alert-danger py-2 small mb-0">{{ formError }}</div>
					</div>
				</div>
			</b-overlay>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" class="me-2" @click="showFormModal = false">Cancel</b-button>
					<b-button class="akkurate-green-btn text-white" :disabled="formLoading" @click="saveSetup">
						<b-spinner v-if="formLoading" small class="me-1" />
						{{ editingId ? "Update" : "Create" }}
					</b-button>
				</div>
			</template>
		</b-modal>

		<!-- View -->
		<b-modal v-model="showViewModal" size="lg" title="Interest setup" hide-footer>
			<div v-if="viewRow" class="small">
				<div class="row g-2">
					<div class="col-md-6"><strong>Name:</strong> {{ viewRow.name }}</div>
					<div class="col-md-6"><strong>Code:</strong> {{ viewRow.code || "—" }}</div>
					<div class="col-12"><strong>Description:</strong> {{ viewRow.description || "—" }}</div>
					<div class="col-md-6"><strong>Status:</strong> {{ viewRow.is_active ? "Active" : "Inactive" }}</div>
					<div class="col-md-6"><strong>Method:</strong> {{ formatMethod(viewRow.interest_method) }}</div>
					<div class="col-md-6"><strong>Flat:</strong> {{ viewRow.interest_flat_amount ?? "—" }}</div>
					<div class="col-md-6"><strong>Percent:</strong> {{ viewRow.interest_percent ?? "—" }}</div>
					<div class="col-md-6"><strong>Min balance:</strong> {{ viewRow.minimum_balance ?? "—" }}</div>
					<div class="col-md-6"><strong>Frequency:</strong> {{ formatFrequency(viewRow.frequency) }}</div>
				</div>
			</div>
		</b-modal>

		<!-- History -->
		<b-modal v-model="showHistoryModal" size="xl" :title="historyTitle" @hidden="resetHistory">
			<b-overlay :show="historyBusy" rounded="sm">
				<p class="small text-muted mb-2">
					Run history for this setup. Use <strong>Pay Interest</strong> to execute a new run.
				</p>
				<b-table
					striped
					bordered
					responsive
					:items="historyRows"
					:fields="historyFields"
					show-empty
					class="table_text_size"
				>
					<template #cell(actions)="data">
						<b-button size="sm" variant="outline-info" @click="openRunDetail(data.item)">
							View
						</b-button>
					</template>
				</b-table>
			</b-overlay>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" @click="showHistoryModal = false">Close</b-button>
				</div>
			</template>
		</b-modal>

		<!-- Run detail + payments -->
		<b-modal v-model="showRunModal" size="xxl" title="Run detail" @hidden="resetRunDetail">
			<b-overlay :show="runBusy" rounded="sm">
				<div class="small mb-2">
					<strong>Run ID:</strong> {{ runDetail?.id || "—" }}
				</div>
				<div class="small mb-3">
					<strong>Period:</strong> {{ runDetail?.period_start || "—" }} → {{ runDetail?.period_end || "—" }}
				</div>
				<div class="small mb-3">
					<strong>Status:</strong> {{ runDetail?.status || "—" }}
				</div>

				<div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
					<div class="d-flex flex-wrap gap-2 align-items-center">
						<b-button
							variant="danger"
							class="btn-size text-white"
							:disabled="!canReverseRun || reversing"
							@click="confirmReverseRun"
						>
							<b-spinner v-if="reversing" small class="me-1" />
							Reverse run
						</b-button>
						<div class="d-flex align-items-center gap-2">
							<label class="small text-muted mb-0">Chunk size</label>
							<b-form-input
								v-model.number="reverseChunkSize"
								type="number"
								min="10"
								max="1000"
								step="1"
								style="max-width: 120px"
								class="field-container"
								:disabled="reversing"
							/>
						</div>
					</div>
					<div v-if="!canReverseRun" class="small text-muted">
						Reverse is only available when status is <strong>completed</strong>.
					</div>
				</div>

				<div v-if="reverseResult" class="border rounded p-2 mb-3 small">
					<div class="d-flex justify-content-between flex-wrap gap-2">
						<div>
							<strong>Reversal stats:</strong>
							reversed {{ reverseResult.stats?.reversed ?? 0 }},
							skipped {{ reverseResult.stats?.skipped ?? 0 }},
							failed {{ reverseResult.stats?.failed ?? 0 }}
						</div>
						<b-button
							v-if="reverseResult.errors && reverseResult.errors.length"
							size="sm"
							variant="outline-secondary"
							@click="showReverseErrors = !showReverseErrors"
						>
							{{ showReverseErrors ? "Hide errors" : "Show errors" }} ({{ reverseResult.errors.length }})
						</b-button>
					</div>
					<b-collapse v-model="showReverseErrors" class="mt-2">
						<b-table
							small
							bordered
							responsive
							:items="reverseResult.errors"
							:fields="[{ key: 'error', label: 'Error' }]"
							show-empty
						>
							<template #cell(error)="d">
								{{ typeof d.item === 'string' ? d.item : (d.item?.error || JSON.stringify(d.item)) }}
							</template>
						</b-table>
					</b-collapse>
				</div>

				<div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
					<h6 class="m-0 fw-bold text-green">Payments</h6>
					<b-button
						class="akkurate-green-btn btn-size text-white"
						:disabled="!selectedSetupId || !runDetail?.id"
						@click="loadPayments(1)"
					>
						Refresh
					</b-button>
				</div>

				<b-table
					striped
					bordered
					responsive
					:items="paymentRows"
					:fields="paymentFields"
					:busy="paymentsBusy"
					show-empty
					class="table_text_size"
				>
					<template #table-busy>
						<div class="text-center text-danger my-2">
							<b-spinner class="align-middle" />
							<strong>Loading...</strong>
						</div>
					</template>
				</b-table>

				<div v-if="paymentsMeta && paymentsMeta.last_page > 1" class="d-flex justify-content-end">
					<b-pagination
						v-model="paymentsPage"
						:total-rows="paymentsMeta.total || 0"
						:per-page="paymentsMeta.per_page || 15"
						@change="loadPayments"
					/>
				</div>
			</b-overlay>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" @click="showRunModal = false">Close</b-button>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ChatBot from "@/views/components/AI/ChatBot.vue";
import ApiService from "@/core/services/api.service";
import Multiselect from "vue-multiselect";

export default {
	name: "InterestSetup",
	components: { SidebarView, TopbarView, ChatBot, Multiselect },
	data() {
		return {
			isBusy: false,
			rows: [],
			currentPage: 1,
			perPage: 15,
			totalRecords: 0,
			perPageOptions: [10, 15, 25, 50, 100].map((n) => ({ value: n, text: String(n) })),
			filters: { filter: "", is_active: "" },
			activeOptions: [
				{ value: "", text: "All" },
				{ value: true, text: "Active" },
				{ value: false, text: "Inactive" },
			],
			tableFields: [
				{ key: "name", label: "Name" },
				{ key: "code", label: "Code" },
				{ key: "is_active", label: "Status" },
				{ key: "interest_method", label: "Method" },
				{ key: "frequency", label: "Frequency" },
				{ key: "actions", label: "Actions" },
			],

			accountTypes: [],
			costOfFundsCoa: [],
			coaSearch: "",

			showFormModal: false,
			formLoading: false,
			editingId: null,
			form: null,
			formError: "",
			formErrorProducts: "",
			formErrorCoa: "",

			showViewModal: false,
			viewRow: null,

			showHistoryModal: false,
			historyBusy: false,
			historyRows: [],
			historyFields: [
				{ key: "id", label: "Run ID" },
				{ key: "period_start", label: "Start" },
				{ key: "period_end", label: "End" },
				{ key: "status", label: "Status" },
				{ key: "created_at", label: "Created" },
				{ key: "actions", label: "Actions" },
			],
			historyTitle: "Run history",
			selectedSetupId: null,

			showRunModal: false,
			runBusy: false,
			reversing: false,
			reverseChunkSize: 200,
			reverseResult: null,
			showReverseErrors: false,
			runDetail: null,
			paymentsBusy: false,
			paymentRows: [],
			paymentsMeta: null,
			paymentsPage: 1,
			paymentFields: [
				{ key: "id", label: "#" },
				{ key: "member_code", label: "Member" },
				{ key: "account_number", label: "Account" },
				{ key: "amount", label: "Amount" },
				{ key: "status", label: "Status" },
				{ key: "created_at", label: "Created" },
			],

			methodOptions: [
				{ value: "flat_amount", text: "Flat amount" },
				{ value: "percent_of_balance", text: "Percent of balance" },
			],
			frequencyOptions: [
				{ value: "daily", text: "Daily" },
				{ value: "weekly", text: "Weekly" },
				{ value: "monthly", text: "Monthly" },
				{ value: "quarterly", text: "Quarterly" },
				{ value: "annually", text: "Annually" },
			],
		};
	},
	created() {
		this.form = this.blankForm();
		this.loadDropdowns();
		this.loadSetups();
	},
	computed: {
		canReverseRun() {
			const s = (this.runDetail?.status || "").toString().toLowerCase();
			return !!this.selectedSetupId && !!this.runDetail?.id && s === "completed";
		},
	},
	methods: {
		formatMethod(m) {
			if (m === "flat_amount") return "Flat amount";
			if (m === "percent_of_balance") return "Percent of balance";
			return m || "—";
		},
		formatFrequency(f) {
			if (!f) return "—";
			if (f === "quarterly") return "Quarterly";
			return String(f).charAt(0).toUpperCase() + String(f).slice(1);
		},
		coaLabel({ code, name, branch, coabranch }) {
			const b = coabranch?.name || branch?.name || "";
			return `${code || ""} — ${name || ""}${b ? ` (${b})` : ""}`;
		},
		async loadDropdowns() {
			try {
				const [acc] = await Promise.all([ApiService.get("/accounts/create/dropdown")]);
				this.accountTypes = acc.data?.account_types || [];
			} catch (e) {
				/* non-blocking */
			}
			this.fetchCostOfFundsCoa("");
		},
		async fetchCostOfFundsCoa(search) {
			this.coaSearch = search || "";
			try {
				const res = await ApiService.get("/interest-setups/coa/cost-of-funds/dropdown", {
					params: this.coaSearch ? { search: this.coaSearch } : undefined,
				});
				this.costOfFundsCoa = res.data?.data || res.data?.data?.data || res.data?.data || [];
			} catch (e) {
				this.costOfFundsCoa = [];
			}
		},
		onCoaSearch(q) {
			this.fetchCostOfFundsCoa(q);
		},
		async loadSetups() {
			this.isBusy = true;
			this.$Progress.start();
			try {
				const res = await ApiService.post("/interest-setups/paginate", {
					page: this.currentPage,
					per_page: this.perPage,
					filter: this.filters.filter || undefined,
					is_active: this.filters.is_active === "" ? undefined : this.filters.is_active,
				});
				const body = res.data;
				this.rows = body.data || body || [];
				this.totalRecords =
					body.meta?.total ??
					body.total ??
					(Array.isArray(this.rows) ? this.rows.length : 0);
			} catch (e) {
				toast.fire({
					icon: "error",
					title: e.response?.data?.message || "Failed to load interest setups",
				});
			} finally {
				this.isBusy = false;
				this.$Progress.finish();
			}
		},
		onPageChange(p) {
			this.currentPage = p;
			this.loadSetups();
		},
		blankForm() {
			return {
				name: "",
				code: "",
				description: "",
				is_active: true,
				interest_method: "flat_amount",
				interest_flat_amount: 0,
				interest_percent: 0,
				minimum_balance: 0,
				frequency: "monthly",
				product_account_types: [],
				debit_coa_subhead: null,
			};
		},
		resetForm() {
			this.formLoading = false;
			this.formError = "";
			this.formErrorProducts = "";
			this.formErrorCoa = "";
			this.form = this.blankForm();
			this.editingId = null;
		},
		openCreateModal() {
			this.editingId = null;
			this.form = this.blankForm();
			this.formError = "";
			this.showFormModal = true;
		},
		async openEditModal(row) {
			this.editingId = row.id;
			this.formError = "";
			this.showFormModal = true;
			this.formLoading = true;
			try {
				const res = await ApiService.get(`/interest-setups/show/${row.id}`);
				const s = res.data?.data || res.data || {};
				this.form = {
					name: s.name || "",
					code: s.code || "",
					description: s.description || "",
					is_active: !!s.is_active,
					interest_method: s.interest_method || "flat_amount",
					interest_flat_amount: Number(s.interest_flat_amount || 0),
					interest_percent: Number(s.interest_percent || 0),
					minimum_balance: Number(s.minimum_balance || 0),
					frequency: s.frequency || "monthly",
					product_account_types:
						(s.product_account_type_ids || []).map((id) =>
							this.accountTypes.find((x) => x.id === id) || { id, name: `#${id}` },
						),
					debit_coa_subhead:
						this.costOfFundsCoa.find((x) => x.id === s.debit_coa_subhead_id) ||
						(s.debit_coa_subhead || null),
				};
			} catch (e) {
				this.formError = e.response?.data?.message || "Could not load setup.";
			} finally {
				this.formLoading = false;
			}
		},
		openViewModal(row) {
			this.viewRow = row;
			this.showViewModal = true;
		},
		validateForm() {
			this.formError = "";
			this.formErrorProducts = "";
			this.formErrorCoa = "";

			if (!this.form.name) return "Name is required.";
			if (!this.form.interest_method) return "Interest method is required.";
			if (!this.form.frequency) return "Frequency is required.";

			if (
				!this.form.product_account_types ||
				!Array.isArray(this.form.product_account_types) ||
				this.form.product_account_types.length === 0
			) {
				this.formErrorProducts = "Please select at least one product type.";
				return "Fix validation errors.";
			}

			if (!this.form.debit_coa_subhead?.id) {
				this.formErrorCoa = "Debit COA is required.";
				return "Fix validation errors.";
			}

			if (this.form.interest_method === "flat_amount") {
				if (!(Number(this.form.interest_flat_amount) > 0)) return "Flat amount must be greater than 0.";
			}
			if (this.form.interest_method === "percent_of_balance") {
				const p = Number(this.form.interest_percent);
				if (!(p > 0 && p <= 100)) return "Percent must be in (0, 100].";
			}

			return "";
		},
		payloadFromForm() {
			return {
				name: this.form.name,
				code: this.form.code || null,
				description: this.form.description || null,
				is_active: !!this.form.is_active,
				interest_method: this.form.interest_method,
				interest_flat_amount: Number(this.form.interest_flat_amount || 0),
				interest_percent: Number(this.form.interest_percent || 0),
				minimum_balance: Number(this.form.minimum_balance || 0),
				frequency: this.form.frequency,
				product_account_type_ids: this.form.product_account_types.map((x) => x.id),
				debit_coa_subhead_id: this.form.debit_coa_subhead.id,
			};
		},
		async saveSetup() {
			const msg = this.validateForm();
			if (msg) {
				this.formError = msg;
				return;
			}
			this.formLoading = true;
			this.formError = "";
			try {
				const payload = this.payloadFromForm();
				if (this.editingId) {
					await ApiService.put(`/interest-setups/update/${this.editingId}`, payload);
					toast.fire({ icon: "success", title: "Interest setup updated" });
				} else {
					await ApiService.post("/interest-setups/store", payload);
					toast.fire({ icon: "success", title: "Interest setup created" });
				}
				this.showFormModal = false;
				this.loadSetups();
			} catch (e) {
				this.formError = e.response?.data?.message || "Save failed.";
			} finally {
				this.formLoading = false;
			}
		},
		confirmDelete(row) {
			swal
				.fire({
					title: "Delete setup?",
					text: `This will permanently delete “${row.name}”.`,
					icon: "warning",
					showCancelButton: true,
					confirmButtonText: "Delete",
					cancelButtonText: "Cancel",
					buttonsStyling: false,
					customClass: {
						confirmButton: "btn btn-size akkurate-danger-btn text-white me-2",
						cancelButton: "btn btn-size akkurate-gray-btn text-white",
					},
				})
				.then(async (r) => {
					if (!r.isConfirmed) return;
					try {
						await ApiService.delete(`/interest-setups/delete/${row.id}`);
						toast.fire({ icon: "success", title: "Deleted" });
						this.loadSetups();
					} catch (e) {
						toast.fire({ icon: "error", title: e.response?.data?.message || "Delete failed" });
					}
				});
		},

		async openHistoryModal(row) {
			this.selectedSetupId = row.id;
			this.historyTitle = `Run history — ${row.name || row.id}`;
			this.showHistoryModal = true;
			this.historyBusy = true;
			try {
				const res = await ApiService.get(`/interest-setups/${row.id}/runs`);
				this.historyRows = res.data?.data || res.data || [];
			} catch (e) {
				this.historyRows = [];
				toast.fire({ icon: "error", title: e.response?.data?.message || "Could not load run history" });
			} finally {
				this.historyBusy = false;
			}
		},
		resetHistory() {
			this.historyRows = [];
			this.historyBusy = false;
			this.selectedSetupId = null;
		},
		async openRunDetail(runRow) {
			if (!this.selectedSetupId || !runRow?.id) return;
			this.showRunModal = true;
			this.runBusy = true;
			this.runDetail = null;
			try {
				const res = await ApiService.get(`/interest-setups/${this.selectedSetupId}/runs/${runRow.id}`);
				this.runDetail = res.data?.data || res.data || runRow;
				await this.loadPayments(1);
			} catch (e) {
				toast.fire({ icon: "error", title: e.response?.data?.message || "Could not load run detail" });
			} finally {
				this.runBusy = false;
			}
		},
		resetRunDetail() {
			this.runDetail = null;
			this.paymentRows = [];
			this.paymentsMeta = null;
			this.paymentsPage = 1;
			this.paymentsBusy = false;
			this.reversing = false;
			this.reverseChunkSize = 200;
			this.reverseResult = null;
			this.showReverseErrors = false;
		},
		confirmReverseRun() {
			if (!this.canReverseRun || this.reversing) return;
			swal
				.fire({
					title: "Reverse interest run?",
					text: "This will reverse all interest credits in this run. Continue?",
					icon: "warning",
					showCancelButton: true,
					confirmButtonText: "Reverse",
					cancelButtonText: "Cancel",
					buttonsStyling: false,
					customClass: {
						confirmButton: "btn btn-size akkurate-danger-btn text-white me-2",
						cancelButton: "btn btn-size akkurate-gray-btn text-white",
					},
				})
				.then(async (r) => {
					if (!r.isConfirmed) return;
					await this.reverseRun();
				});
		},
		async refreshRunDetail() {
			if (!this.selectedSetupId || !this.runDetail?.id) return;
			try {
				const res = await ApiService.get(
					`/interest-setups/${this.selectedSetupId}/runs/${this.runDetail.id}`,
				);
				this.runDetail = res.data?.data || res.data || this.runDetail;
			} catch (e) {
				/* non-blocking */
			}
		},
		async reverseRun() {
			if (!this.canReverseRun) return;
			this.reversing = true;
			this.reverseResult = null;
			this.showReverseErrors = false;
			this.$Progress.start();
			try {
				const chunk = Number(this.reverseChunkSize);
				const body =
					Number.isFinite(chunk) && chunk >= 10 && chunk <= 1000
						? { chunk_size: chunk }
						: { chunk_size: 200 };
				const res = await ApiService.post(
					`/interest-setups/${this.selectedSetupId}/runs/${this.runDetail.id}/reverse`,
					body,
				);
				const data = res.data || {};
				this.reverseResult = data;
				toast.fire({
					icon: data.success ? "success" : "info",
					title: data.message || "Reversal completed",
				});
				await this.refreshRunDetail();
				await this.loadPayments(1);
			} catch (e) {
				const msg =
					e.response?.data?.message ||
					(e.response?.status === 422
						? "Only completed runs can be reversed."
						: "Reversal failed.");
				toast.fire({ icon: "error", title: msg });
			} finally {
				this.reversing = false;
				this.$Progress.finish();
			}
		},
		async loadPayments(page) {
			if (!this.selectedSetupId || !this.runDetail?.id) return;
			this.paymentsBusy = true;
			try {
				const p = page || 1;
				const res = await ApiService.get(
					`/interest-setups/${this.selectedSetupId}/runs/${this.runDetail.id}/payments`,
					{ params: { page: p, per_page: 15 } },
				);
				const body = res.data;
				this.paymentRows = body.data || body || [];
				this.paymentsMeta = body.meta || null;
				this.paymentsPage = p;
			} catch (e) {
				this.paymentRows = [];
				this.paymentsMeta = null;
			} finally {
				this.paymentsBusy = false;
			}
		},
	},
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

